/*
 * Purpose: Compose pipeline — upcoming / now / history stage resolution
 * Last modified: 2026-06-28 | Agent
 * Completeness: 95
 */
import type { AutoBlocksPhase } from '@/services/autoblocks/types';
import type { ArtifactState } from '@/services/autoblocks/missionTypes';

export type ComposeStageId =
  | 'describe'
  | 'interpret'
  | 'confirm'
  | 'plan'
  | 'review'
  | 'dry_run'
  | 'approve';

export interface ComposePipelineStage {
  id: ComposeStageId;
  label: string;
  shortLabel: string;
}

/** Fixed patient order — every compose session follows this rail */
export const COMPOSE_PIPELINE: ComposePipelineStage[] = [
  { id: 'describe', label: 'Describe your outcome', shortLabel: 'Describe' },
  { id: 'interpret', label: 'Interpret your request', shortLabel: 'Interpret' },
  { id: 'confirm', label: 'Confirm & choose mode', shortLabel: 'Confirm' },
  { id: 'plan', label: 'Plan your Mission map', shortLabel: 'Plan' },
  { id: 'review', label: 'Review plan & connect', shortLabel: 'Review' },
  { id: 'dry_run', label: 'Dry run test', shortLabel: 'Dry run' },
  { id: 'approve', label: 'Approve Flow at rest', shortLabel: 'Approve' },
];

export interface ComposeTimelineState {
  phase: AutoBlocksPhase;
  artifactState: ArtifactState;
  hasPlan: boolean;
  hasDryRunReport: boolean;
  isDryRunning: boolean;
}

export interface ComposeTimelineView {
  current: ComposePipelineStage;
  currentIndex: number;
  upcoming: ComposePipelineStage[];
  completed: ComposePipelineStage[];
  isComplete: boolean;
}

function stageIndex(id: ComposeStageId): number {
  return COMPOSE_PIPELINE.findIndex((s) => s.id === id);
}

export function resolveCurrentStageId(state: ComposeTimelineState): ComposeStageId {
  const { phase, artifactState, hasPlan, hasDryRunReport, isDryRunning } = state;

  if (artifactState === 'flow' || phase === 'approved') return 'approve';
  if (isDryRunning || phase === 'dry_run') return 'dry_run';
  if (hasDryRunReport && hasPlan) return 'approve';
  if (hasPlan && (phase === 'review' || phase === 'dry_run')) return 'review';
  if (phase === 'director' || phase === 'planning' || phase === 'express_running') return 'plan';
  if (phase === 'confirm') return 'confirm';
  if (phase === 'interpreting') return 'interpret';
  return 'describe';
}

export function buildComposeTimelineView(state: ComposeTimelineState): ComposeTimelineView {
  const currentId = resolveCurrentStageId(state);
  const currentIndex = stageIndex(currentId);
  const current = COMPOSE_PIPELINE[currentIndex]!;
  const isComplete = state.artifactState === 'flow' || state.phase === 'approved';

  return {
    current,
    currentIndex,
    upcoming: isComplete ? [] : COMPOSE_PIPELINE.slice(currentIndex + 1),
    completed: COMPOSE_PIPELINE.slice(0, currentIndex),
    isComplete,
  };
}
